import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { Box } from "../../shared/components/ui/chakra";
import Button from "../../shared/components/ui/Button";
import InputError from "../../shared/components/ui/InputError";
import Radio from "../../shared/components/ui/Radio";
import Textarea from "../../shared/components/ui/Textarea";
import PostResourceAvatar from "../../shared/components/app/PostResourceAvatar";
import PostResourceDisplayName from "../../shared/components/app/PostResourceDisplayName";
import SendIcon from "../../shared/components/icons/SendIcon";
import { SharedCurrentUser } from "../../shared/lib/types";
import FileUploadButton from "../../../shared/components/FileUploadButton";
import AddIcon from "../../shared/components/icons/AddIcon";
import UploadFiles, {
  useUploadFiles,
} from "../../../shared/components/UploadFiles";
import { postCommentRepliesPath } from "../../../../routes";
import useRequest from "../../shared/lib/useRequest";
import { uploadFiles } from "../../../shared/lib/uploadFile";
import useFlash from "../../shared/lib/useFlash";
import { PostComment, PostsShowProps } from "../lib/types";
import { Stack } from "@mui/material";

import { Flex, HStack, RadioGroup } from "@chakra-ui/react";

const schema = yup.object().shape({
  content: yup.string().trim().required().max(65535),
  anonymous: yup.string().when("$isRequredAnonymous", {
    is: true,
    then: (schema) => schema.required("表示名を選択してください"),
  }),
});

const ReplyForm = ({
  post,
  comment,
  currentUser,
  onSubmitted,
}: {
  post: PostsShowProps;
  comment: PostComment;
  currentUser: SharedCurrentUser;
  onSubmitted: () => void;
}) => {
  const [active, setActive] = useState(false);
  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isValid },
    reset,
  } = useForm({
    defaultValues: { content: "", anonymous: post.previous_post_anonymous },
    resolver: yupResolver(schema),
    context: { isRequredAnonymous: currentUser?.is_anonymousable },
  });
  const [files, addFiles, removeFile, clear] = useUploadFiles();
  const request = useRequest();
  const flash = useFlash();
  const anonymous = watch("anonymous");

  const isAnonymous = anonymous === "1";
  const onSubmit = async (data: yup.InferType<typeof schema>) => {
    const result = await uploadFiles({ files } as { files: File[] });
    if (result.some((res) => !!res.error)) {
      flash({
        error: "ファイルアップロードに失敗しました。もう一度お試しください",
      });
      return;
    }
    const res = await request(postCommentRepliesPath(comment.code), "POST", {
      post_comment_reply: {
        ...data,
        files: result.map((res) => res.blob!.signed_id),
      },
    });
    if (res.ok) {
      clear();
      reset({ content: "", anonymous });
      onSubmitted();
    }
  };

  return (
    <Box
      as="form"
      onFocus={() => setActive(true)}
      onSubmit={handleSubmit(onSubmit)}
      data-testid="new-reply-form"
    >
      {active && currentUser?.is_all_public_feature_accessible && (
        <Stack gap={2}>
          {currentUser.is_anonymousable && (
            <Controller
              control={control}
              name="anonymous"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Box ref={ref} tabIndex={-1}>
                  <RadioGroup {...field}>
                    <HStack gap={4}>
                      <Radio value={"0"}>実名で返信</Radio>
                      <Radio value={"1"}>ニックネームで返信</Radio>
                    </HStack>
                  </RadioGroup>
                  {error && <InputError>{error.message}</InputError>}
                </Box>
              )}
            />
          )}
          <HStack mb={2} gap={2}>
            <PostResourceAvatar
              boxSize={10}
              author={{
                ...currentUser,
                display_name: isAnonymous
                  ? currentUser.nickname
                  : currentUser.full_name,
                display_image_url: isAnonymous
                  ? currentUser.anonymous_image_url
                  : currentUser.profile_image_url,
                avatar_bgcolor: isAnonymous
                  ? currentUser.anonymous_avatar_bgcolor
                  : currentUser.real_name_avatar_bgcolor,
                anonymous: isAnonymous,
              }}
            />
            <PostResourceDisplayName
              author={
                isAnonymous
                  ? {
                    display_name: currentUser.nickname,
                    anonymous: isAnonymous,
                    is_guest: false,
                    floor_age: currentUser.floor_age,
                    architect_type: currentUser.architect_type,
                  }
                  : {
                    display_name: currentUser.full_name,
                    anonymous: isAnonymous,
                    is_guest: currentUser.is_guest,
                  }
              }
            />
          </HStack>
        </Stack>
      )}
      <Flex gap={2}>
        <Controller
          control={control}
          name="content"
          render={({ field }) => (
            <Textarea
              h={12}
              border="none"
              rows={active ? 5 : 1}
              borderRadius={8}
              placeholder="返信を入力"
              py={"14px"}
              autoExpand
              autoExpandMaxHeightPx={160}
              bgColor={active ? "brand.100" : "gray.light"}
              _placeholder={{ color: "gray.base" }}
              {...field}
              onFocus={() => setActive(true)}
            />
          )}
        />
        <Button
          boxSize={12}
          borderRadius={4}
          type="submit"
          isLoading={isSubmitting}
          // isDisabledを設定せず、colorだけを変えている理由
          // disable状態でクリックした際もエラーを表示させるため
          // https://fusic.backlog.jp/view/A_LOOP-186#comment-1488931772
          color={isValid ? "primary" : "disabled"}
          aria-disabled={!isValid}
          aria-label="返信"
        >
          <SendIcon boxSize={5} />
        </Button>
      </Flex>
      {active && (
        <Box color="textGray" fontSize="xs" mt={1}>
          ※出典（引用元）を記載するとコメントの信憑性が高まります。
        </Box>
      )}
      {active && (
        <Box mt={2}>
          <FileUploadButton multiple onChange={addFiles} name="files">
            <HStack
              gap={0}
              textDecoration="underline"
              color="textLink"
              _hover={{ textDecoration: "none" }}
            >
              <AddIcon />
              <Box fontSize="xs">ファイル添付</Box>
            </HStack>
          </FileUploadButton>
          <UploadFiles uploadFiles={files} removeFile={removeFile} />
        </Box>
      )}
    </Box>
  );
};

export default ReplyForm;
